import { useState } from "react";
import { enablePush, recheckPush, pushConfigured, type PushOutcome } from "../lib/push";
import { pushStep, isIOS } from "../lib/platform";
import { api } from "../lib/api";
import { InstallSteps, InstallWhy } from "./InstallSteps";

// The notification setup card, shared by Onboarding and Home so a member sees
// the same instructions wherever they hit it. Branches on what the person can
// actually do right now — offering a "turn on notifications" button in iPhone
// Safari just fails silently, which is what used to happen.
export function PushSetup({ leagueId, onDone }: { leagueId: string; onDone?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PushOutcome | null>(null);
  const [testState, setTestState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [testNote, setTestNote] = useState("");
  const step = pushStep();

  async function turnOn() {
    setBusy(true);
    const r = await enablePush(leagueId);
    setResult(r);
    setBusy(false);
    if (r === "push_ready") onDone?.();
  }

  async function recheck() {
    setBusy(true);
    const r = await recheckPush();
    setResult(r);
    setBusy(false);
    if (r === "push_ready") onDone?.();
  }

  // Proof. "You're set" was an assertion nobody could check, and the only way to
  // confirm a real push landed was to ask the commissioner to send you one.
  async function sendTest() {
    setTestState("sending");
    setTestNote("");
    try {
      const r = await api<{ note?: string }>(`/api/league/admin`, {
        body: { op: "test-push", leagueId },
      });
      setTestState("sent");
      setTestNote(r?.note ?? "");
    } catch (e: any) {
      setTestState("failed");
      setTestNote(e?.message ?? "We couldn't send it. Try again in a moment.");
    }
  }

  if (!pushConfigured()) return null;

  if (result === "push_ready") {
    return (
      <div className="card live">
        <span className="tag">Notifications are on</span>
        <p>You&apos;re set. Your phone will ping you the second questions drop. 🔔</p>
        {testState !== "sent" && (
          <>
            <p className="muted">Want to be sure? Send yourself one right now.</p>
            <button className="btn ghost" disabled={testState === "sending"} onClick={sendTest}>
              {testState === "sending" ? "Sending…" : "Send me a test notification"}
            </button>
          </>
        )}
        {testState === "sent" && (
          <p className="muted">
            Sent. It should land in a second or two. If your phone stays quiet, close the app first and
            send another, since some phones hold notifications back while you are looking at the screen.
          </p>
        )}
        {testState === "failed" && <p className="error">{testNote}</p>}
      </div>
    );
  }

  // --- Chrome/Firefox/Edge on iPhone: they need Safari before anything works ---
  if (step === "wrong_browser") {
    return (
      <div className="card live">
        <span className="tag">Open this in Safari first</span>
        <p>
          You&apos;re in another browser right now. On iPhone, only <b>Safari</b> can turn this into a real
          app that pings you. That&apos;s an Apple rule, not ours.
        </p>
        <ol className="steps">
          <li>Copy the web address at the top of this screen.</li>
          <li>Open the <b>Safari</b> app.</li>
          <li>Paste the address in and go.</li>
          <li>Then follow the steps there to add it to your home screen.</li>
        </ol>
      </div>
    );
  }

  // --- iPhone Safari: installing is the only way forward ---
  if (step === "install_first") {
    return (
      <div className="card live">
        <span className="tag">Step 1: Put it on your home screen</span>
        <p>Takes about 15 seconds.</p>
        <InstallSteps />
        <InstallWhy />
        <p className="muted">Once you open it from the icon, come back here and turn on notifications.</p>
      </div>
    );
  }

  if (step === "unsupported") {
    return (
      <div className="card">
        <span className="tag">Notifications</span>
        <p>This browser can&apos;t do notifications. Open the app on drop days so you don&apos;t miss anything.</p>
      </div>
    );
  }

  // --- Installed (or Android/desktop): we can actually ask ---
  return (
    <div className="card live">
      <span className="tag">{isIOS() ? "Step 2: Turn on notifications" : "Turn on notifications"}</span>
      <p>Questions drop at a random time. The ping on your phone is how you find out. Without it, you&apos;ll miss them.</p>
      <button className="btn accent" disabled={busy} onClick={turnOn}>
        {busy ? "Turning on…" : "TURN ON NOTIFICATIONS"}
      </button>
      <p className="muted">Your phone will ask if you want to allow notifications. Tap <b>Allow</b>.</p>

      {/* Permission granted, registration still landing. This is a normal wait,
          especially on iPhone, and it used to be reported as a flat failure with
          a wall of settings instructions the person did not need. */}
      {result === "pending" && (
        <div>
          <p className="muted">
            Your phone said yes. It&apos;s still finishing up with Apple, which takes a few seconds.
          </p>
          <button className="btn ghost" disabled={busy} onClick={recheck}>
            {busy ? "Checking…" : "Check again"}
          </button>
        </div>
      )}

      {(result === "denied" || result === "failed") && (
        <div>
          <p className="muted">
            {result === "denied"
              ? "Your phone said no. Once that's been refused, only the Settings app can turn it back on."
              : "That didn't take. Your phone said no somewhere. Check both of these:"}
          </p>
          {isIOS() ? (
            <p className="muted">
              Settings app → Notifications → Homie League → turn on Allow Notifications. Then come back
              here and try again.
            </p>
          ) : (
            <>
              <p className="muted">
                <b>1.</b> Press and hold the Homie League icon → <b>Site settings</b> → <b>Notifications</b> →
                turn on.
              </p>
              <p className="muted">
                <b>2.</b> Settings app → Apps → Homie League → Notifications → turn on.
              </p>
              <p className="muted">
                These two look the same but they are different settings, and the first one is the one
                people miss. Turn on both, then come back here and try again.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
